import { isBillCheckingTargeted, isSaveTargeted } from "../../lib/auth/utils";
import type { FlowState } from "../../lib/flow-engine";
import type { OnboardingConfig } from "./OnboardingFlowV3";
import type { Intent, OnboardingIdentity } from "./types";

export type OnboardingStateData = {
  hasFaAnswers?: boolean;
  hasIdentity?: boolean;
  hasInsurance?: boolean;
  identity?: OnboardingIdentity | null;
  /**
   * Segmentation features; intent is read from here. Sourced from the same
   * fresh network-only query as the other flags — not the auth-context user
   * snapshot, whose freshness depends on the wrapper's load ordering.
   */
  features?: readonly string[] | null;
  /** From `viewerPartner` — null when the user did not join via a partner. */
  partner?: {
    name: string;
    slug: string;
  } | null;
};

export type DeriveInitialInput = {
  /** Canonical presence flags; null when the caller could not be resolved. */
  state: OnboardingStateData | null;
  /**
   * Mirrors `OnboardingContext.skipFaFlow`: general-user entry never resumes
   * into the FA screener (`hasFaAnswers` is not consulted) — a save-intent
   * user with no FA answers resumes at Insurance instead.
   */
  skipFaFlow: boolean;
  /** The entry screen this mount would use with nothing persisted. */
  topOfFunnel: FlowState<OnboardingConfig>;
};

export type DerivedInitial = {
  tag: "flow";
  initial: FlowState<OnboardingConfig>;
};

// FlowState literals (the engine's documented `initial` shape) instead of
// `Screen.X()` constructors keep this module free of the flow's React imports.
export function deriveInitial({
  state,
  skipFaFlow,
  topOfFunnel,
}: DeriveInitialInput): DerivedInitial {
  const features = state?.features;
  // `undefined`, not `null` — must match the shape Profile threads down.
  const identity = state?.identity ?? undefined;
  const intent: Intent | undefined = isBillCheckingTargeted(features)
    ? "bill"
    : isSaveTargeted(features)
      ? "save"
      : undefined;

  // No identity yet (or caller unresolved: state === null) → start at the
  // supplied top-of-funnel.
  if (state === null || !state.hasIdentity) {
    return { tag: "flow", initial: topOfFunnel };
  }

  if (intent === undefined) {
    return { tag: "flow", initial: { tag: "Intent", data: { identity } } };
  }
  if (intent === "save" && !skipFaFlow && !state.hasFaAnswers) {
    return {
      tag: "flow",
      initial: { tag: "FinancialAssistance", data: { intent, identity } },
    };
  }
  if (!state.hasInsurance) {
    return {
      tag: "flow",
      initial: { tag: "Insurance", data: { intent, identity } },
    };
  }
  return { tag: "flow", initial: { tag: "Complete", data: {} } };
}
